// inv/src/app/app-components/deals-modules/dealsTable.jsx
import { useEffect, useMemo, useState, memo } from 'react';
import { Link } from 'react-router-dom';
import Timeline from '../../../components/ui/lastdeals1';
import InvestorLogo from '../../../components/data/InvestorLogo';
import { fetchInvestorById } from '../crm-modules/apiService';
import { useTranslation } from 'react-i18next';
import { useDealCardCollection } from '../../../hooks/deal-card';
import { buildInvestorProfilePath } from '../../../components/investor-display/helpers';

// Small shimmer and skeleton card for the timeline
const Shimmer = memo(function Shimmer({ className = '' }) {
    return <div className={`bg-stone-200/80 rounded animate-pulse ${className}`} />;
  });
  
  const SkeletonDealCard = memo(function SkeletonDealCard() {
    return (
      <li>
        {/* mobile */}
        <div
          className="md:hidden grid items-start"
          style={{ gridTemplateColumns: '12px minmax(0,1fr)', columnGap: '12px' }}
        >
          <div className="relative" />
          <div className="rounded-md bg-stone-50 p-3 border border-stone-200">
            <Shimmer className="h-5 w-56 rounded" />
            <div className="mt-3 flex items-center gap-2">
              <Shimmer className="h-7 w-28 rounded-full" />
              <Shimmer className="h-7 w-16 rounded-full" />
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Shimmer className="h-7 w-16 rounded-full" />
              <Shimmer className="h-7 w-24 rounded-full" />
              <Shimmer className="h-7 w-20 rounded-full" />
            </div>
            <div className="mt-3 space-y-2">
              <Shimmer className="h-3 w-full rounded" />
              <Shimmer className="h-3 w-5/6 rounded" />
              <Shimmer className="h-3 w-2/3 rounded" />
            </div>
          </div>
        </div>
  
        {/* desktop */}
        <div
          className="hidden md:grid items-start"
          style={{ gridTemplateColumns: '16px 180px 80px minmax(0,1fr)', columnGap: '12px' }}
        >
          <div className="relative" />
          <div className="col-[2/5] rounded-md bg-stone-50 p-6 border border-stone-200">
            <div className="grid" style={{ gridTemplateColumns: '180px minmax(0,1fr)', columnGap: '12px' }}>
              <div className="flex flex-col items-start">
                <Shimmer className="h-7 w-40 rounded-full" />
                <div className="mt-2">
                  <Shimmer className="h-7 w-24 rounded-full" />
                </div>
                <div className="mt-2">
                  <Shimmer className="h-7 w-28 rounded-full" />
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Shimmer className="h-7 w-20 rounded-full" />
                  <Shimmer className="h-7 w-28 rounded-full" />
                  <Shimmer className="h-7 w-24 rounded-full" />
                </div>
              </div>
              <div className="grid" style={{ gridTemplateColumns: 'minmax(0,1fr) 120px', columnGap: '12px' }}>
                <Shimmer className="h-7 w-3/4 rounded" />
                <Shimmer className="h-7 w-full rounded" />
                <div className="mt-4 col-[1/2] space-y-2">
                  <Shimmer className="h-4 w-28 rounded" />
                  <div className="flex flex-wrap gap-2">
                    <Shimmer className="h-6 w-28 rounded-full" />
                    <Shimmer className="h-6 w-24 rounded-full" />
                    <Shimmer className="h-6 w-20 rounded-full" />
                  </div>
                </div>
                <div className="mt-4 col-[1/2] space-y-2">
                  <Shimmer className="h-4 w-36 rounded" />
                  <div className="flex flex-wrap gap-2">
                    <Shimmer className="h-6 w-24 rounded-full" />
                    <Shimmer className="h-6 w-28 rounded-full" />
                  </div>
                </div>
                <div className="mt-4 col-[1/2] space-y-2">
                  <Shimmer className="h-3 w-full rounded" />
                  <Shimmer className="h-3 w-5/6 rounded" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </li>
    );
  });
  
  const DealsTimelineView = ({ deals = [], isLoading = false, pageSize = 10 }) => {
  const { t, i18n } = useTranslation();

  const [investorMap, setInvestorMap] = useState({}); // key: DB _id -> { id, name, country }

  // Collect ALL ids (lead + other) and load missing ones
  useEffect(() => {
    if (isLoading) return;
    if (!Array.isArray(deals) || deals.length === 0) return;
    const ids = new Set();
    deals.forEach(d => {
      (d.lead_investors || []).forEach(id => ids.add(id));
      (d.other_investors_participated || []).forEach(id => ids.add(id));
    });

    const missing = [...ids].filter(_id => !investorMap[_id]);
    if (missing.length === 0) return;

    Promise
      .all(missing.map((_id) =>
        fetchInvestorById(_id) // should return { id, name, country, _id? }
          .then(inv => inv ? { _id, id: inv.id, name: inv.name, country: inv.country } : null)
          .catch(() => null)
      ))
      .then(list => {
        const updated = { ...investorMap };
        list.forEach(p => {
          if (p) updated[p._id] = { id: p.id, name: p.name, country: p.country };
        });
        setInvestorMap(updated);
      });
  }, [deals, investorMap, isLoading]);

  const makeInvestorProfileUrl = (inv) => {
    return buildInvestorProfilePath({
      investor: inv,
      language: i18n.language,
      inApp: true,
    });
  };

  const chip = (dbId) => {
    const inv = investorMap[dbId];
    if (!inv) return dbId; // in case it has not loaded yet
    return (
      <Link key={dbId} to={makeInvestorProfileUrl(inv)} className="inline-flex items-center gap-1 hover:underline">
        <InvestorLogo id={inv.id} country={inv.country} className="w-4 h-4" alt={`${inv.name} logo`} />
        <span>{inv.name}</span>
      </Link>
    );
  };

  const { items, surfaceConfig } = useDealCardCollection({
    deals,
    surfaceKey: 'appDeals',
    buildInvestorNode: chip,
  });

  const moreText = t('investorCard.readMore', { defaultValue: 'Read more' });
  const lessText = t('investorCard.showLess', { defaultValue: 'Read less' });

    if (isLoading) {
        return (
          <ol className="relative z-10 space-y-4 md:space-y-6">
            {Array.from({ length: pageSize }).map((_, i) => (
             <SkeletonDealCard key={`deal-skel-${i}`} />
            ))}
          </ol>
        );
      }
    
      if (!items.length) {
    return <div className="text-center text-gray-500">No deals available</div>;
  }

  return (
    <Timeline
      items={items}
      moreText={moreText}
      lessText={lessText}
      recipientAccess={surfaceConfig.recipientCompany.accessMode}
      className="max-w-full"
    />
  );
};

export default DealsTimelineView;
